import type { PostChain } from "./styles";

// Post-processing (#75): a style's `post` chain, cut down by the quality
// setting. This is the pure part (tested); `render/composer.ts` makes the GPU
// objects through three's EffectComposer, and `Stage` loads it with a dynamic
// `import()` the first time a chain isn't empty, so a build that only ever
// draws cartoon (or Low) doesn't download it.

/** What one frame draws after the scene, at a quality. */
export interface ActivePasses {
  ao: PostChain["ao"];
  bloom: PostChain["bloom"];
  grade: PostChain["grade"];
  tiltShift: PostChain["tiltShift"];
  mood: PostChain["mood"];
  dof: PostChain["dof"];
}

/**
 * The passes a style draws at a quality. Null draws none at all (straight to
 * the canvas). Low always draws none: a grade alone still cost a multisampled
 * half-float target and two full-screen passes, which made realistic Low ~35%
 * slower than realistic with no chain (health check 2026-09-28), and Low is
 * the phone default.
 */
export function activePasses(post: PostChain, quality: "low" | "high"): ActivePasses | null {
  if (quality === "low") return null;
  const passes: ActivePasses = { ao: post.ao, bloom: post.bloom, grade: post.grade, tiltShift: post.tiltShift, mood: post.mood, dof: post.dof };
  return Object.values(passes).some((p) => p !== null) ? passes : null;
}

/** A shadow map size at a quality: Low halves it (not below 1024). */
export function shadowMapSize(size: number, quality: "low" | "high"): number {
  return quality === "high" ? size : Math.max(1024, size / 2);
}

/** What `Stage` needs from a composer (the replay test swaps in a stub). */
export interface Composer {
  render(): void;
  setSize(w: number, h: number): void;
  /** Depth of field (#179): sharp `distance` metres out, blurring off it by `k` (0 is off). */
  setFocus(distance: number, k: number): void;
  dispose(): void;
}
